import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { SizeProperty } from '@src/types';

interface SizePropertiesTableProps {
  data: SizeProperty[];
}

export const SizePropertiesTable: React.FC<SizePropertiesTableProps> = ({ data }) => {
  const navigate = useNavigate();

  const getStatus = (property: SizeProperty): 'complete' | 'incomplete' => {
    // A size property is complete if it has display and at least one family group
    const hasDisplay = !!property.display;
    const hasGroup = property.family && property.family.length > 0;
    return hasDisplay && hasGroup ? 'complete' : 'incomplete';
  };

  const handleRowClick = (property: SizeProperty) => {
    navigate(`/catalog/attributes/sizes/${property.id}`);
  };

  const getDimensions = (property: SizeProperty): string => {
    const parts: string[] = [];
    if (property.primary_value) {
      const label = property.primary_label || 'Primary';
      parts.push(`${label}: ${property.primary_value}`);
    }
    if (property.secondary_value) {
      const label = property.secondary_label || 'Secondary';
      parts.push(`${label}: ${property.secondary_value}`);
    }
    if (property.tertiary_value) {
      const label = property.tertiary_label || 'Tertiary';
      parts.push(`${label}: ${property.tertiary_value}`);
    }
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Code
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Display Name
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Size Groups
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Dimensions
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Synonyms
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((property) => {
            const status = getStatus(property);
            return (
              <TableRow
                key={property.id}
                hover
                onClick={() => handleRowClick(property)}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <TableCell>{property.code}</TableCell>
                <TableCell>{property.display}</TableCell>
                <TableCell>
                  {property.family && property.family.length > 0
                    ? property.family.join(', ')
                    : '—'}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {getDimensions(property)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {property.synonyms && property.synonyms.length > 0
                    ? property.synonyms.join(', ')
                    : '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={status === 'complete' ? 'Complete' : 'Incomplete'}
                    color={status === 'complete' ? 'success' : 'warning'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
